import '../scrollytelling.css'
import '../v3/office.css'
import '../v4/human.css'
import '../v5/kombi.css'
import './baukasten.css'

import {
  ArrowDownIcon,
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  CheckIcon,
  LinkIcon,
  UserGroupIcon
} from '@heroicons/react/24/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { SceneProvider, useScrollStory } from '../engine'
import { BeatsProvider } from '../v3/beats'
import { atB, inB } from '../v5/beats'
import KombiStory, { camera as baseCamera } from '../v5/KombiStory'
import { TextModeProvider } from '../v8/Headline'
import { LINK_EXTRA } from './extras/link'
import { MESSE } from './extras/messe'
import { TEAM_EXTRA } from './extras/team'
import { WANDEL_EXTRA } from './extras/wandel'
import { composeTimeline } from './timeline'

/**
 * Version 7 „Baukasten“: Vor dem Start wählst du, welche Extras in die
 * Geschichte sollen. Die Grundgeschichte aus Version 5 bleibt gleich, die
 * Extras werden als eigene Scrollstrecke hinter der passenden Station
 * eingeschoben und spielen im selben Büro (die Messe in der Halle nebenan).
 */

const EXTRAS = [MESSE, TEAM_EXTRA, LINK_EXTRA, WANDEL_EXTRA]
const ICONS = {
  messe: BuildingStorefrontIcon,
  team: UserGroupIcon,
  link: LinkIcon,
  wandel: CalendarDaysIcon
}
const BEATS_VALUE = { inB, atB }

const readChosen = () => {
  const raw = new URLSearchParams(window.location.search).get('x')
  if (raw == null) return EXTRAS.map((e) => e.id)
  return raw.split(',').filter((id) => EXTRAS.some((e) => e.id === id))
}

export default function ScrollytellingV7() {
  return <Baukasten />
}

/**
 * Gemeinsamer Kern von Version 7 und 8. `textLayer` ersetzt die Captions der
 * Grundgeschichte, `textMode` schaltet die Extras auf Headlines um.
 */
export function Baukasten({
  textMode = 'caption',
  textLayer,
  centerY,
  className
}) {
  const [chosen, setChosen] = useState(readChosen)

  const toggle = (id) =>
    setChosen((prev) =>
      prev.includes(id)
        ? prev.filter((c) => c !== id)
        : EXTRAS.map((e) => e.id).filter((e) => e === id || prev.includes(e))
    )

  // Auswahl in ?x= merken, so lässt sich eine Zusammenstellung teilen
  useEffect(() => {
    const url = new URL(window.location.href)
    url.searchParams.set('x', chosen.join(','))
    window.history.replaceState(null, '', url)
  }, [chosen])

  const start = () => {
    const el = document.getElementById('kx-story')
    if (!el) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY,
      behavior: reduce ? 'auto' : 'smooth'
    })
  }

  const extras = useMemo(
    () => EXTRAS.filter((e) => chosen.includes(e.id)),
    [chosen]
  )

  return (
    <TextModeProvider value={textMode}>
      <div className={clsx('sty-root off-root hum-root kx-root', className)}>
        <Configurator chosen={chosen} onToggle={toggle} onStart={start} />
        <Story
          key={chosen.join(',')}
          extras={extras}
          textLayer={textLayer}
          centerY={centerY}
        />
      </div>
    </TextModeProvider>
  )
}

// ---------- Auswahl vor dem Start ----------

function Configurator({ chosen, onToggle, onStart }) {
  return (
    <section className='kx-config' aria-labelledby='kx-config-title'>
      <div className='kx-config__inner'>
        <div className='off-eyebrow'>Recruiting mit Aivy</div>
        <h1 id='kx-config-title' className='kx-config__title'>
          Stell dir deine Geschichte zusammen.
        </h1>
        <p className='kx-config__lead'>
          Die Grundgeschichte läuft immer, von der offenen Stelle bis zum neuen
          Teammitglied. Wähle dazu, was du zusätzlich sehen willst. Alles läuft
          danach in einem Rutsch, einfach weiterscrollen.
        </p>

        <div className='kx-base-card'>
          <CheckIcon width={16} height={16} />
          <div>
            <b>Grundgeschichte</b>
            <span>
              Erst ohne Aivy, dann mit. Anforderungen, Game-based Assessment,
              Passung, Kreuzmatching und Einstellung.
            </span>
          </div>
        </div>

        <div className='kx-options' role='group' aria-label='Extras'>
          {EXTRAS.map((ex) => {
            const on = chosen.includes(ex.id)
            const Icon = ICONS[ex.id]
            return (
              <button
                key={ex.id}
                type='button'
                role='checkbox'
                aria-checked={on}
                className={clsx('kx-option', on && 'is-on')}
                onClick={() => onToggle(ex.id)}
              >
                <span className='kx-option__icon'>
                  <Icon width={22} height={22} />
                </span>
                <span className='kx-option__text'>
                  <b>{ex.title}</b>
                  <span>{ex.teaser}</span>
                </span>
                <span className='kx-option__check'>
                  {on && <CheckIcon width={14} height={14} />}
                </span>
              </button>
            )
          })}
        </div>

        <button type='button' className='off-cta kx-start' onClick={onStart}>
          Geschichte starten <ArrowDownIcon width={16} height={16} />
        </button>
      </div>
    </section>
  )
}

Configurator.propTypes = {
  chosen: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired
}

// ---------- Geschichte mit eingeschobenen Extras ----------

function Story({ extras, textLayer, centerY }) {
  const story = useScrollStory(1, { mode: 'pin' })
  const timeline = useMemo(() => composeTimeline(extras), [extras])
  const state = useRef(timeline.map(0))
  const rootRef = useRef(null)
  const baseRef = useRef(null)
  const [active, setActive] = useState(0)
  const { subscribe } = story

  // Grundgeschichte bekommt ihre eigene Zeit t, steht während eines Extras
  const baseSubscribe = useCallback(
    (index, cb) =>
      subscribe(index, (l) => {
        const s = timeline.map(l)
        state.current = s
        cb(s.t)
      }),
    [subscribe, timeline]
  )

  const extraSubscribe = useMemo(
    () =>
      Object.fromEntries(
        extras.map((ex) => [
          ex.id,
          (index, cb) => subscribe(index, (l) => cb(timeline.map(l).p[ex.id]))
        ])
      ),
    [extras, subscribe, timeline]
  )

  // CSS-Variablen und Zustand für alles, was ohne React läuft
  useEffect(() => {
    const root = rootRef.current
    const locals = [...root.querySelectorAll('[data-local]')]
    return subscribe(0, (l) => {
      const s = timeline.map(l)
      baseRef.current?.style.setProperty('--l', s.t.toFixed(5))
      locals.forEach((el) =>
        el.style.setProperty('--l', (s.p[el.dataset.local] ?? 0).toFixed(5))
      )
      root.dataset.extra = s.extra || ''
      const i = timeline.chapters.findIndex((c) => l >= c.at[0] && l < c.at[1])
      setActive(i === -1 ? timeline.chapters.length - 1 : i)
    })
  }, [subscribe, timeline])

  const cameraOverride = useCallback(() => {
    const s = state.current
    if (!s.extra) return null
    const ex = extras.find((e) => e.id === s.extra)
    return ex.camera(s.local, baseCamera(s.t))
  }, [extras])

  const jump = (i) => {
    const section = rootRef.current?.querySelector('.off-section')
    if (!section) return
    const top = section.getBoundingClientRect().top + window.scrollY
    const [a, b] = timeline.chapters[i].at
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({
      top:
        top + (a + (b - a) * 0.5) * (section.offsetHeight - window.innerHeight),
      behavior: reduce ? 'auto' : 'smooth'
    })
  }

  const names = extras.map((e) => e.label)
  const lead = names.length
    ? `Grundgeschichte plus ${names.length === 1 ? 'ein Extra' : `${names.length} Extras`}: ${names.join(', ')}. Scroll los.`
    : 'Die Grundgeschichte, ohne Extras. Scroll los.'
  const chapter = timeline.chapters[active]

  return (
    <div ref={rootRef} id='kx-story' className='kx-story'>
      <div ref={story.storyRef}>
        <section
          ref={(el) => (story.chapterRefs.current[0] = el)}
          className='off-section'
          style={{ height: `${timeline.total}vh` }}
          aria-label='Deine Geschichte mit Aivy'
        >
          <div
            ref={(el) => (story.sceneRefs.current[0] = el)}
            className='off-sticky'
          >
            <div ref={baseRef} className='kx-base' style={{ '--l': 0 }}>
              <SceneProvider index={0} subscribe={baseSubscribe}>
                <BeatsProvider value={BEATS_VALUE}>
                  <KombiStory
                    title='Deine Geschichte mit Aivy.'
                    lead={lead}
                    hideNav
                    textLayer={textLayer}
                    centerY={centerY}
                    cameraOverride={cameraOverride}
                    worldExtras={extras.map((ex) => (
                      <SceneProvider
                        key={ex.id}
                        index={0}
                        subscribe={extraSubscribe[ex.id]}
                      >
                        <div
                          className='kx-local'
                          data-local={ex.id}
                          style={{ '--l': 0 }}
                        >
                          <ex.World />
                        </div>
                      </SceneProvider>
                    ))}
                    screenExtras={
                      <>
                        {extras.map((ex) => (
                          <SceneProvider
                            key={ex.id}
                            index={0}
                            subscribe={extraSubscribe[ex.id]}
                          >
                            <div
                              className='kx-local kx-local--screen'
                              data-local={ex.id}
                              style={{ '--l': 0 }}
                            >
                              <ex.Screen />
                            </div>
                          </SceneProvider>
                        ))}
                        <nav className='off-nav kx-nav' aria-label='Kapitel'>
                          {timeline.chapters.map((c, i) => (
                            <button
                              key={`${c.id}-${i}`}
                              type='button'
                              className={clsx(
                                'off-nav__dot',
                                c.extra && 'kx-nav__extra',
                                i === active && 'is-active'
                              )}
                              onClick={() => jump(i)}
                              aria-label={c.label}
                              title={c.label}
                            />
                          ))}
                          <span className='off-nav__label'>
                            {chapter.extra && (
                              <span className='kx-extra-badge'>Extra</span>
                            )}{' '}
                            {chapter.label}
                          </span>
                        </nav>
                      </>
                    }
                  />
                </BeatsProvider>
              </SceneProvider>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

Story.propTypes = {
  extras: PropTypes.arrayOf(PropTypes.object).isRequired,
  textLayer: PropTypes.func,
  centerY: PropTypes.number
}

Baukasten.propTypes = {
  textMode: PropTypes.oneOf(['caption', 'headline']),
  textLayer: PropTypes.func,
  centerY: PropTypes.number,
  className: PropTypes.string
}
